import {
  buildReportChartSemanticInspector,
  type ReportChartSemanticInspectorModel,
} from "./chart-semantic-inspector";
import {
  buildUniverFormulaOracleModel,
  type CogoUniverFormulaOracleModel,
} from "./univer-formula-oracle";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import {
  getReportWorkspaceModelCopy,
  type ReportWorkspaceModelCopy,
} from "./report-workspace-copy";

export const REPORT_EXPORT_TARGETS = ["pdf", "docx", "pptx", "hwpx"] as const;

export type ReportExportTarget = (typeof REPORT_EXPORT_TARGETS)[number];
export type ReportWorkspaceStatus = "ready" | "warn" | "blocked" | "missing";

/**
 * Production-input modes and the absence sentinels this model writes into
 * FACT values (`Mode`, `Upload`, `Analysis`, `Oracle`, `Writer state`, …).
 *
 * These are SCHEMA VOCABULARY, not copy: the operator matches them against
 * the evidence payload they read elsewhere, so they render untranslated in
 * every locale — the same boundary `data-inspector.tsx` documents for element
 * types. Exported as a runtime array so the report-workspace i18n gate can
 * DERIVE its allowlist from the declaration instead of restating it, and so a
 * later sweep that translates one of them turns that gate red.
 */
export const REPORT_PRODUCTION_INPUT_MODES = [
  "platform-upload",
  "fixture",
  "embedded-evidence",
  "missing",
] as const;

export type ReportProductionInputMode =
  (typeof REPORT_PRODUCTION_INPUT_MODES)[number];

/** Absence sentinels this model writes into fact values. Schema vocabulary — see above. */
export const REPORT_MODEL_FACT_SENTINELS = [
  "missing",
  "fixture-only",
  "documentExportProductionGate",
  "documentModelSummary",
  // `documentExportServiceManifest` is deliberately NOT here: it appears only
  // in `evidenceRefs`, which no surface renders, so listing it would be an
  // allowlist entry that excludes nothing — the i18n gate's
  // `every allowlist term earns its place` check found it and said so.
] as const;

export interface ReportWorkspaceEvidenceItem {
  id: string;
  kind:
    | "native-artifact"
    | "artifact-readback"
    | "native-detail"
    | "visual-residual"
    | "chart-readback"
    | "semantic-readback";
  label: string;
  status: ReportWorkspaceStatus;
  ref: string;
  url?: string;
  sha256?: string;
  sizeBytes?: number;
  detail?: string;
}

export interface ReportWorkspacePage {
  id: string;
  label: string;
  pageIndex: number;
  status: ReportWorkspaceStatus;
  nodeCount: number;
  paragraphCount?: number;
  tableCount?: number;
  pictureCount?: number;
  width?: number;
  height?: number;
  detail: string;
  evidenceRef: string;
}

export interface ReportWorkspaceTarget {
  format: ReportExportTarget;
  status: ReportWorkspaceStatus;
  artifactId?: string;
  artifactUrl?: string;
  artifactDownloadPath?: string;
  artifactName?: string;
  artifactMimeType?: string;
  artifactSizeBytes?: number;
  artifactSha256?: string;
  serviceArtifactPass?: boolean;
  artifactReadbackPass?: boolean;
  writerPass: boolean;
  readbackPass: boolean;
  renderPass: boolean;
  pageCountPass: boolean;
  productionParityPass: boolean;
  semanticReadbackPass: boolean;
  oracle: string;
  writerState: string;
  visualQuality: {
    status: ReportWorkspaceStatus;
    strictDiffRatio?: number;
    strictMaxPageDiffRatio?: number;
    rendererFloorDiffRatio?: number;
    rendererFloorMaxPageDiffRatio?: number;
    rendererFloorTotalLimit?: number;
    rendererFloorPageLimit?: number;
    rendererFloorPass?: boolean;
    primaryResidualKind: string;
    primaryResidualIssue: string;
    topResidualPage?: number;
    residualEvidenceRef: string;
    visualSheetPath?: string;
    blockers: string[];
  };
  nativeDetail: {
    status: ReportWorkspaceStatus;
    componentDetailStatus: string;
    componentDetailCoverageReady: boolean;
    componentSummary: string;
    tableConversionRules: string;
    tableConversionProfiles: string;
    borderPrimitiveSummary: string;
    tableAuthorityPass?: boolean;
    borderPrimitiveAuthorityPass?: boolean;
    evidenceRef: string;
    blockers: string[];
  };
  evidenceItems: ReportWorkspaceEvidenceItem[];
  blockers: string[];
}

export interface ReportWorkspaceModel {
  schemaVersion: "studio-report-workspace-model.v1";
  status: ReportWorkspaceStatus;
  title: string;
  sourceKind: string;
  pageCount: number;
  pages: ReportWorkspacePage[];
  nodeCount: number;
  targetCount: number;
  readyTargetCount: number;
  selectedTarget: ReportExportTarget;
  targets: ReportWorkspaceTarget[];
  semanticEdit: {
    pass: boolean;
    expectedText: string;
    operation: string;
    replacementCount: number;
  };
  artifactService: {
    status: ReportWorkspaceStatus;
    schemaVersion: string;
    operationId: string;
    artifactCount: number;
    passArtifactCount: number;
    readbackPassCount: number;
    formats: string[];
    downloadRoutePrefix: string;
    storageAuthority: string;
  };
  formulaOracle: CogoUniverFormulaOracleModel;
  chartInspector: ReportChartSemanticInspectorModel;
  productionInput: {
    status: ReportWorkspaceStatus;
    mode: ReportProductionInputMode;
    uploadId: string;
    uploadFileName: string;
    analysisJobId: string;
    analysisStatus: string;
    platformAnalysisReady: boolean;
    normalizedModelReady: boolean;
    evidenceRef: string;
    blockers: string[];
  };
  exportHistory: Array<{
    id: string;
    label: string;
    target: ReportExportTarget;
    status: ReportWorkspaceStatus;
    fileName: string;
    sizeBytes: number;
    sha256: string;
    downloadPath: string;
    artifactUrl?: string;
  }>;
  lanes: Array<{
    id: string;
    label: string;
    status: ReportWorkspaceStatus;
    detail: string;
  }>;
  exportBlocked: boolean;
  publishBlocked: boolean;
  blockers: string[];
  evidenceRefs: string[];
}

export interface ReportWorkspaceExportArtifact {
  schemaVersion: "studio-report-workspace-export-artifact.v1";
  generatedAt: string;
  target: ReportExportTarget;
  title: string;
  sourceKind: string;
  status: ReportWorkspaceStatus;
  pageCount: number;
  pages: ReportWorkspacePage[];
  nodeCount: number;
  semanticEdit: ReportWorkspaceModel["semanticEdit"];
  formulaOracle: ReportWorkspaceModel["formulaOracle"];
  chartInspector: ReportWorkspaceModel["chartInspector"];
  productionInput: ReportWorkspaceModel["productionInput"];
  targetReadiness: ReportWorkspaceTarget;
  blockers: string[];
  evidenceRefs: string[];
  nativeArtifact?: {
    url?: string;
    fileName?: string;
    mimeType?: string;
  };
  note: string;
}

export function isReportNativeExportReady(
  model: ReportWorkspaceModel,
): boolean {
  if (model.targetCount === 0) return false;
  if (model.readyTargetCount !== model.targetCount) return false;
  if (!model.semanticEdit.pass) return false;
  return (
    model.targets.length === model.targetCount &&
    model.targets.every(
      (target) =>
        target.status === "ready" &&
        target.writerPass &&
        target.readbackPass &&
        target.renderPass &&
        target.pageCountPass &&
        target.productionParityPass &&
        target.semanticReadbackPass,
    )
  );
}

export function reportNativeExportGateStatus(
  model: ReportWorkspaceModel,
): ReportWorkspaceStatus {
  if (isReportNativeExportReady(model)) return "ready";
  if (model.targetCount === 0 || model.targets.length === 0) return "missing";
  if (!model.semanticEdit.pass) return "blocked";
  return model.targets.some((target) => target.status === "blocked")
    ? "blocked"
    : "warn";
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function readArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function readUrl(value: unknown): string | null {
  const text = readString(value);
  if (!text) return null;
  try {
    const url = new URL(text);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function readStringList(value: unknown): string[] {
  return readArray(value).flatMap((entry) => {
    const text = readString(entry);
    return text ? [text] : [];
  });
}

function readArtifactRecord(
  target: Record<string, unknown>,
): Record<string, unknown> | null {
  return (
    readRecord(target.generatedArtifact) ??
    readRecord(target.artifact) ??
    readRecord(target.output) ??
    readRecord(target.download) ??
    null
  );
}

function basenameFromPath(value: unknown): string | undefined {
  const text = readString(value);
  if (!text) return undefined;
  const normalized = text.split(/[?#]/)[0] ?? text;
  const parts = normalized.split(/[\\/]/).filter(Boolean);
  return parts.at(-1) || undefined;
}

function mimeTypeForTarget(format: ReportExportTarget): string {
  if (format === "pdf") return "application/pdf";
  if (format === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (format === "pptx") {
    return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  }
  return "application/hwp+zip";
}

function readArtifactUrl(target: Record<string, unknown>): string | undefined {
  const artifact = readArtifactRecord(target);
  return (
    readUrl(target.artifactUrl) ??
    readUrl(target.downloadUrl) ??
    readUrl(target.outputUrl) ??
    readUrl(target.url) ??
    readUrl(artifact?.artifactUrl) ??
    readUrl(artifact?.downloadUrl) ??
    readUrl(artifact?.outputUrl) ??
    readUrl(artifact?.url) ??
    readUrl(artifact?.href) ??
    undefined
  );
}

function readArtifactName(target: Record<string, unknown>): string | undefined {
  const artifact = readArtifactRecord(target);
  return (
    readString(target.artifactName) ??
    readString(target.fileName) ??
    readString(target.filename) ??
    readString(artifact?.artifactName) ??
    readString(artifact?.fileName) ??
    readString(artifact?.filename) ??
    basenameFromPath(readRecord(target.writer)?.outputPath) ??
    basenameFromPath(readRecord(target.writer)?.artifactPath) ??
    basenameFromPath(readRecord(target.writer)?.downloadPath) ??
    undefined
  );
}

function readArtifactDownloadPath(
  target: Record<string, unknown>,
): string | undefined {
  const artifact = readArtifactRecord(target);
  return (
    readString(target.downloadPath) ??
    readString(target.outputPath) ??
    readString(target.path) ??
    readString(artifact?.downloadPath) ??
    readString(artifact?.outputPath) ??
    readString(artifact?.path) ??
    readString(readRecord(target.writer)?.downloadPath) ??
    readString(readRecord(target.writer)?.artifactPath) ??
    readString(readRecord(target.writer)?.outputPath) ??
    undefined
  );
}

function readArtifactMimeType(
  target: Record<string, unknown>,
): string | undefined {
  const artifact = readArtifactRecord(target);
  return (
    readString(target.mimeType) ??
    readString(target.contentType) ??
    readString(artifact?.mimeType) ??
    readString(artifact?.contentType) ??
    undefined
  );
}

function readReportWorkspaceServiceManifest(
  project: unknown,
): Record<string, unknown> | null {
  const candidate = readFirstDeep(project, [
    ["documentExportServiceManifest"],
    ["documentExportArtifactManifest"],
    ["documentExportArtifacts"],
    ["reportExportServiceManifest"],
    ["generationEvidence", "documentExportServiceManifest"],
    ["generationEvidence", "documentExportArtifactManifest"],
    ["generationEvidence", "documentExportArtifacts"],
    ["generationEvidence", "reportExportServiceManifest"],
    ["metadata", "documentExportServiceManifest"],
    ["metadata", "documentExportArtifactManifest"],
    ["metadata", "documentExportArtifacts"],
    ["metadata", "reportExportServiceManifest"],
    ["manifest", "documentExportServiceManifest"],
    ["manifest", "documentExportArtifactManifest"],
    ["manifest", "documentExportArtifacts"],
    ["manifest", "reportExportServiceManifest"],
  ]);
  if (Array.isArray(candidate)) return { artifacts: candidate };
  return readRecord(candidate);
}

function readDocumentExportServiceBaseUrl(
  project: unknown,
  serviceManifest: Record<string, unknown> | null,
): string | undefined {
  const candidate = readFirstDeep(project, [
    ["documentExportServiceBaseUrl"],
    ["documentExportArtifactBaseUrl"],
    ["reportExportServiceBaseUrl"],
    ["generationEvidence", "documentExportServiceBaseUrl"],
    ["generationEvidence", "documentExportArtifactBaseUrl"],
    ["generationEvidence", "reportExportServiceBaseUrl"],
    ["metadata", "documentExportServiceBaseUrl"],
    ["metadata", "documentExportArtifactBaseUrl"],
    ["metadata", "reportExportServiceBaseUrl"],
    ["manifest", "documentExportServiceBaseUrl"],
    ["manifest", "documentExportArtifactBaseUrl"],
    ["manifest", "reportExportServiceBaseUrl"],
  ]);
  return (
    readUrl(candidate) ??
    readUrl(serviceManifest?.baseUrl) ??
    readUrl(serviceManifest?.serviceBaseUrl) ??
    readUrl(serviceManifest?.productionBaseUrl) ??
    readUrl(serviceManifest?.artifactBaseUrl) ??
    readUrl(readRecord(serviceManifest?.route)?.baseUrl) ??
    readUrl(readRecord(serviceManifest?.serviceResponse)?.baseUrl) ??
    undefined
  );
}

function joinArtifactUrl(
  baseUrl: string | undefined,
  pathOrUrl: unknown,
): string | undefined {
  const direct = readUrl(pathOrUrl);
  if (direct) return direct;
  const path = readString(pathOrUrl);
  if (!path || !baseUrl) return undefined;
  try {
    return new URL(path, baseUrl).toString();
  } catch {
    return undefined;
  }
}

function serviceArtifactUrl(
  artifact: Record<string, unknown> | null | undefined,
  baseUrl: string | undefined,
): string | undefined {
  if (!artifact) return undefined;
  return (
    joinArtifactUrl(baseUrl, artifact.artifactUrl) ??
    joinArtifactUrl(baseUrl, artifact.downloadUrl) ??
    joinArtifactUrl(baseUrl, artifact.outputUrl) ??
    joinArtifactUrl(baseUrl, artifact.url) ??
    joinArtifactUrl(baseUrl, artifact.href) ??
    joinArtifactUrl(baseUrl, artifact.downloadPath) ??
    joinArtifactUrl(baseUrl, artifact.path) ??
    undefined
  );
}

function serviceArtifactName(
  artifact: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!artifact) return undefined;
  return (
    readString(artifact.artifactName) ??
    readString(artifact.fileName) ??
    readString(artifact.filename) ??
    basenameFromPath(artifact.relativePath) ??
    basenameFromPath(artifact.outputPath) ??
    basenameFromPath(artifact.downloadPath) ??
    basenameFromPath(artifact.path) ??
    undefined
  );
}

function serviceArtifactDownloadPath(
  artifact: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!artifact) return undefined;
  return (
    readString(artifact.downloadPath) ??
    readString(artifact.outputPath) ??
    readString(artifact.path) ??
    readString(artifact.relativePath) ??
    undefined
  );
}

function serviceArtifactMimeType(
  artifact: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!artifact) return undefined;
  return (
    readString(artifact.mimeType) ??
    readString(artifact.contentType) ??
    readString(artifact.mimetype) ??
    undefined
  );
}

function serviceArtifactMap(
  serviceManifest: Record<string, unknown> | null,
): Map<ReportExportTarget, Record<string, unknown>> {
  if (!serviceManifest) return new Map();
  const artifacts = [
    ...readArray(serviceManifest.artifacts),
    ...readArray(readRecord(serviceManifest.serviceResponse)?.artifacts),
  ];
  const entries = artifacts.flatMap((entry) => {
    const record = readRecord(entry);
    const format = normalizeTarget(record?.format);
    return record && format ? ([[format, record]] as const) : [];
  });
  return new Map(entries);
}

function readServiceArtifactSummary(
  serviceManifest: Record<string, unknown> | null,
): ReportWorkspaceModel["artifactService"] {
  if (!serviceManifest) {
    return {
      status: "missing",
      schemaVersion: "missing",
      operationId: "missing",
      artifactCount: 0,
      passArtifactCount: 0,
      readbackPassCount: 0,
      formats: [],
      downloadRoutePrefix: "missing",
      storageAuthority: "missing",
    };
  }
  const summary = readRecord(serviceManifest.summary);
  const artifacts = serviceArtifactMap(serviceManifest);
  const artifactCount = readNumber(summary?.artifactCount) ?? artifacts.size;
  const passArtifactCount =
    readNumber(summary?.passArtifactCount) ??
    [...artifacts.values()].filter((artifact) => artifact.pass === true).length;
  const readbackPassCount =
    readNumber(summary?.readbackPassCount) ??
    [...artifacts.values()].filter(
      (artifact) => readRecord(artifact.readback)?.pass === true,
    ).length;
  const formats =
    readStringList(summary?.formats).length > 0
      ? readStringList(summary?.formats)
      : [...artifacts.keys()];
  const pass = readBoolean(serviceManifest.pass) ?? false;
  return {
    status: pass && artifactCount > 0 ? "ready" : "blocked",
    schemaVersion:
      readString(serviceManifest.schemaVersion) ?? "unknown-service-manifest",
    operationId: readString(serviceManifest.operationId) ?? "not recorded",
    artifactCount,
    passArtifactCount,
    readbackPassCount,
    formats,
    downloadRoutePrefix:
      readString(summary?.downloadRoutePrefix) ??
      readString(
        readRecord(serviceManifest.route)?.artifactDownloadPathPrefix,
      ) ??
      "not recorded",
    storageAuthority:
      readString(summary?.storageAuthority) ??
      readString(serviceManifest.storageAuthority) ??
      "service-manifest",
  };
}

function serviceExportHistory(
  serviceManifest: Record<string, unknown> | null,
  artifactBaseUrl: string | undefined,
  copy: ReportWorkspaceModelCopy,
): ReportWorkspaceModel["exportHistory"] {
  if (!serviceManifest) return [];
  const artifacts = serviceArtifactMap(serviceManifest);
  return [...artifacts.entries()].map(([target, artifact], index) => ({
    id: readString(artifact.artifactId) ?? `${target}-artifact-${index + 1}`,
    label: copy.evidenceNativeArtifact(target.toUpperCase()),
    target,
    status: artifact.pass === true ? "ready" : "blocked",
    fileName:
      serviceArtifactName(artifact) ?? `cogo-report-${target}.${target}`,
    sizeBytes: readNumber(artifact.sizeBytes) ?? 0,
    sha256: readString(artifact.sha256) ?? "",
    downloadPath: readString(artifact.downloadPath) ?? "not recorded",
    ...(serviceArtifactUrl(artifact, artifactBaseUrl)
      ? { artifactUrl: serviceArtifactUrl(artifact, artifactBaseUrl) }
      : {}),
  }));
}

function readDeep(source: unknown, path: string[]): unknown {
  let current: unknown = source;
  for (const key of path) {
    const record = readRecord(current);
    if (!record) return undefined;
    current = record[key];
  }
  return current;
}

function readFirstDeep(source: unknown, paths: string[][]): unknown {
  for (const path of paths) {
    const value = readDeep(source, path);
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function containsReportFixtureMarker(value: unknown, depth = 0): boolean {
  if (depth > 6 || value === null || value === undefined) return false;
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    return (
      normalized.includes("gridone") || normalized.includes("gridone-cogo-app")
    );
  }
  if (typeof value !== "object") return false;
  if (Array.isArray(value)) {
    return value.some((entry) => containsReportFixtureMarker(entry, depth + 1));
  }
  const record = value as Record<string, unknown>;
  if (readBoolean(record.localTestOnly) === true) return true;
  if (readString(record.sourceCorpus)?.toLowerCase() === "gridone") return true;
  return Object.values(record).some((entry) =>
    containsReportFixtureMarker(entry, depth + 1),
  );
}

function readReportUploadEvidence(project: unknown): {
  record: Record<string, unknown> | null;
  ref: string;
} {
  const paths = [
    ["reportUpload"],
    ["documentUpload"],
    ["uploadedDocument"],
    ["sourceUpload"],
    ["generationEvidence", "reportUpload"],
    ["generationEvidence", "documentUpload"],
    ["generationEvidence", "uploadedDocument"],
    ["metadata", "reportUpload"],
    ["metadata", "documentUpload"],
    ["metadata", "uploadedDocument"],
    ["manifest", "reportUpload"],
    ["manifest", "documentUpload"],
    ["manifest", "uploadedDocument"],
  ];
  for (const path of paths) {
    const value = readRecord(readDeep(project, path));
    if (value) return { record: value, ref: path.join(".") };
  }
  return { record: null, ref: "missing" };
}

function readReportPlatformAnalysisEvidence(project: unknown): {
  record: Record<string, unknown> | null;
  ref: string;
} {
  const paths = [
    ["platformDocumentAnalysis"],
    ["documentAnalysis"],
    ["documentAnalysisJob"],
    ["reportAnalysis"],
    ["reportAnalysisJob"],
    ["generationEvidence", "platformDocumentAnalysis"],
    ["generationEvidence", "documentAnalysis"],
    ["generationEvidence", "documentAnalysisJob"],
    ["generationEvidence", "reportAnalysis"],
    ["metadata", "platformDocumentAnalysis"],
    ["metadata", "documentAnalysis"],
    ["metadata", "documentAnalysisJob"],
    ["metadata", "reportAnalysis"],
    ["manifest", "platformDocumentAnalysis"],
    ["manifest", "documentAnalysis"],
    ["manifest", "documentAnalysisJob"],
    ["manifest", "reportAnalysis"],
  ];
  for (const path of paths) {
    const value = readRecord(readDeep(project, path));
    if (value) return { record: value, ref: path.join(".") };
  }
  return { record: null, ref: "missing" };
}

function analysisStatusReady(status: string): boolean {
  return [
    "ready",
    "completed",
    "complete",
    "succeeded",
    "success",
    "pass",
    "passed",
  ].includes(status.toLowerCase());
}

function buildReportProductionInput(
  project: unknown,
  evidence: Record<string, unknown> | null,
  serviceManifest: Record<string, unknown> | null,
  copy: ReportWorkspaceModelCopy,
): ReportWorkspaceModel["productionInput"] {
  const upload = readReportUploadEvidence(project);
  const analysis = readReportPlatformAnalysisEvidence(project);
  const uploadRecord = upload.record;
  const analysisRecord = analysis.record;
  const fixtureMode =
    containsReportFixtureMarker(
      readFirstDeep(project, [
        ["metadata", "cogoMeta"],
        ["manifest", "cogoMeta"],
        ["cogoMeta"],
      ]),
    ) ||
    containsReportFixtureMarker(evidence?.sourceRoot) ||
    containsReportFixtureMarker(serviceManifest?.sourceRoot) ||
    containsReportFixtureMarker(readRecord(evidence?.source)) ||
    containsReportFixtureMarker(readRecord(serviceManifest?.source));
  if (fixtureMode) {
    return {
      status: "blocked",
      mode: "fixture",
      uploadId: "fixture",
      uploadFileName: "fixture",
      analysisJobId: "fixture",
      analysisStatus: "fixture-only",
      platformAnalysisReady: false,
      normalizedModelReady: false,
      evidenceRef: "fixture/gridone",
      blockers: [copy.blockerFixturePromotion],
    };
  }

  const uploadId =
    readString(uploadRecord?.uploadId) ??
    readString(uploadRecord?.fileId) ??
    readString(uploadRecord?.artifactId) ??
    readString(analysisRecord?.uploadId) ??
    "missing";
  const uploadFileName =
    readString(uploadRecord?.fileName) ??
    readString(uploadRecord?.filename) ??
    readString(uploadRecord?.name) ??
    "missing";
  const analysisJobId =
    readString(analysisRecord?.analysisJobId) ??
    readString(analysisRecord?.jobId) ??
    readString(analysisRecord?.operationId) ??
    readString(analysisRecord?.id) ??
    "missing";
  const analysisStatus =
    readString(analysisRecord?.status) ??
    (readBoolean(analysisRecord?.pass) === true ? "pass" : "missing");
  const platformAnalysisReady =
    readBoolean(analysisRecord?.pass) === true ||
    readBoolean(analysisRecord?.ready) === true ||
    analysisStatusReady(analysisStatus);
  const normalizedModelReady =
    readBoolean(analysisRecord?.normalizedModelReady) ??
    readBoolean(analysisRecord?.cogoJsonReady) ??
    readBoolean(analysisRecord?.sidecarReady) ??
    false;
  const blockers = uniqueStrings(
    [
      ...readStringList(analysisRecord?.blockers),
      uploadId === "missing" ? copy.blockerUploadIdMissing : null,
      analysisJobId === "missing" ? copy.blockerAnalysisJobIdMissing : null,
      !platformAnalysisReady ? copy.blockerAnalysisNotReady : null,
      !normalizedModelReady ? copy.blockerNormalizedNotReady : null,
    ].filter((value): value is string => Boolean(value)),
  );

  if (uploadRecord || analysisRecord) {
    return {
      status: blockers.length === 0 ? "ready" : "blocked",
      mode: "platform-upload",
      uploadId,
      uploadFileName,
      analysisJobId,
      analysisStatus,
      platformAnalysisReady,
      normalizedModelReady,
      evidenceRef:
        [upload.ref, analysis.ref]
          .filter((entry) => entry !== "missing")
          .join(" + ") || "missing",
      blockers,
    };
  }

  if (evidence) {
    return {
      status: "warn",
      mode: "embedded-evidence",
      uploadId: "missing",
      uploadFileName: "missing",
      analysisJobId: "missing",
      analysisStatus: "embedded-evidence",
      platformAnalysisReady: false,
      normalizedModelReady: false,
      evidenceRef: "documentExportProductionGate",
      blockers: [copy.blockerCommercialRequiresUpload],
    };
  }

  return {
    status: "missing",
    mode: "missing",
    uploadId: "missing",
    uploadFileName: "missing",
    analysisJobId: "missing",
    analysisStatus: "missing",
    platformAnalysisReady: false,
    normalizedModelReady: false,
    evidenceRef: "missing",
    blockers: [copy.blockerUploadAndAnalysisMissing],
  };
}

function normalizeTarget(format: unknown): ReportExportTarget | null {
  const text = readString(format)?.toLowerCase();
  return REPORT_EXPORT_TARGETS.find((target) => target === text) ?? null;
}

function statusFromBoolean(
  pass: boolean | null | undefined,
): ReportWorkspaceStatus {
  return pass === true ? "ready" : "blocked";
}

function mergeStatus(statuses: ReportWorkspaceStatus[]): ReportWorkspaceStatus {
  if (statuses.includes("blocked")) return "blocked";
  if (statuses.includes("missing")) return "missing";
  if (statuses.includes("warn")) return "warn";
  return "ready";
}

function readReportWorkspaceStatus(
  value: unknown,
): ReportWorkspaceStatus | null {
  const text = readString(value)?.toLowerCase();
  return text === "ready" ||
    text === "warn" ||
    text === "blocked" ||
    text === "missing"
    ? text
    : null;
}

function readFirstNonEmptyArray(...values: unknown[]): unknown[] {
  for (const value of values) {
    const array = readArray(value);
    if (array.length > 0) return array;
  }
  return [];
}

function buildReportWorkspacePages(
  project: unknown,
  evidence: Record<string, unknown> | null,
  summary: Record<string, unknown> | null,
  pageCount: number,
  copy: ReportWorkspaceModelCopy,
): ReportWorkspacePage[] {
  const source = readRecord(summary?.source);
  const fallbackEvidenceRef =
    readString(source?.layoutGraphRef) ??
    readString(source?.documentModelRef) ??
    readString(readRecord(evidence?.documentModel)?.id) ??
    "documentModelSummary";
  const pageRecords = readFirstNonEmptyArray(
    summary?.pages,
    evidence?.pages,
    evidence?.documentPages,
    readRecord(evidence?.documentModel)?.pages,
    readRecord(evidence?.layoutGraph)?.pages,
    readDeep(project, ["documentModel", "pages"]),
    readDeep(project, ["layoutGraph", "pages"]),
    readDeep(project, ["metadata", "documentModel", "pages"]),
    readDeep(project, ["metadata", "layoutGraph", "pages"]),
    readDeep(project, [
      "metadata",
      "reportSourceSidecar",
      "documentModel",
      "pages",
    ]),
    readDeep(project, [
      "metadata",
      "reportSourceSidecar",
      "layoutGraph",
      "pages",
    ]),
    readDeep(project, [
      "metadata",
      "reportAnalysisCogoApp",
      "documentModel",
      "pages",
    ]),
    readDeep(project, ["generationEvidence", "documentModel", "pages"]),
    readDeep(project, ["generationEvidence", "layoutGraph", "pages"]),
  );

  const pagesFromEvidence = pageRecords.flatMap((entry, index) => {
    const record = readRecord(entry);
    if (!record) return [];
    const pageIndex =
      readNumber(record.pageIndex) ??
      readNumber(record.index) ??
      readNumber(record.page) ??
      index + 1;
    const nodeCount =
      readNumber(record.nodeCount) ??
      readNumber(readRecord(record.integrity)?.nodeCount) ??
      readArray(record.nodes).length;
    const paragraphCount = readNumber(record.paragraphCount);
    const tableCount = readNumber(record.tableCount);
    const pictureCount = readNumber(record.pictureCount);
    const width =
      readNumber(record.width) ??
      readNumber(readRecord(record.pageSetup)?.width) ??
      readNumber(readRecord(readRecord(record.pageSetup)?.px)?.width);
    const height =
      readNumber(record.height) ??
      readNumber(readRecord(record.pageSetup)?.height) ??
      readNumber(readRecord(readRecord(record.pageSetup)?.px)?.height);
    const detailParts = [
      copy.countNodes(nodeCount),
      paragraphCount !== null ? copy.countParagraphs(paragraphCount) : null,
      tableCount !== null ? copy.countTables(tableCount) : null,
      pictureCount !== null ? copy.countPictures(pictureCount) : null,
    ].filter((value): value is string => Boolean(value));

    return [
      {
        id: readString(record.id) ?? `page-${pageIndex}`,
        label:
          readString(record.title) ??
          readString(record.label) ??
          copy.pageLabel(pageIndex),
        pageIndex,
        status: readReportWorkspaceStatus(record.status) ?? "ready",
        nodeCount,
        ...(paragraphCount !== null ? { paragraphCount } : {}),
        ...(tableCount !== null ? { tableCount } : {}),
        ...(pictureCount !== null ? { pictureCount } : {}),
        ...(width !== null ? { width } : {}),
        ...(height !== null ? { height } : {}),
        detail: readString(record.detail) ?? detailParts.join(" · "),
        evidenceRef:
          readString(record.evidenceRef) ??
          readString(record.sourceRef) ??
          fallbackEvidenceRef,
      },
    ];
  });

  if (pagesFromEvidence.length > 0) {
    const seen = new Set<number>();
    const deduped = pagesFromEvidence
      .sort((left, right) => left.pageIndex - right.pageIndex)
      .filter((page) => {
        if (seen.has(page.pageIndex)) return false;
        seen.add(page.pageIndex);
        return true;
      });
    if (pageCount <= deduped.length) return deduped;
    const known = new Set(deduped.map((page) => page.pageIndex));
    return [
      ...deduped,
      ...Array.from({ length: pageCount }, (_, index) => index + 1)
        .filter((pageIndex) => !known.has(pageIndex))
        .map((pageIndex) =>
          fallbackReportWorkspacePage(pageIndex, fallbackEvidenceRef, copy),
        ),
    ].sort((left, right) => left.pageIndex - right.pageIndex);
  }

  return Array.from({ length: pageCount }, (_, index) =>
    fallbackReportWorkspacePage(index + 1, fallbackEvidenceRef, copy),
  );
}

function fallbackReportWorkspacePage(
  pageIndex: number,
  evidenceRef: string,
  copy: ReportWorkspaceModelCopy,
): ReportWorkspacePage {
  return {
    id: `page-${pageIndex}`,
    label: copy.pageLabel(pageIndex),
    pageIndex,
    status: "warn",
    nodeCount: 0,
    detail: copy.pageDetailNotAttached,
    evidenceRef,
  };
}

function summarizeCountRecord(value: unknown): string {
  const record = readRecord(value);
  if (!record) return "none";
  const entries = Object.entries(record)
    .filter(([, count]) => typeof count === "number" && Number.isFinite(count))
    .sort((left, right) => {
      const delta = Number(right[1]) - Number(left[1]);
      return delta || left[0].localeCompare(right[0]);
    });
  return entries.length
    ? entries.map(([key, count]) => `${key}:${count}`).join(", ")
    : "none";
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))];
}

function primaryContributionLabel(value: unknown): string {
  const record = readRecord(value);
  if (!record) return "not recorded";
  const entries = Object.entries(record).flatMap(([key, raw]) => {
    const contribution = readRecord(raw);
    const ratio = readNumber(contribution?.ratioOfPageDiff);
    const pixels = readNumber(contribution?.diffPixels);
    if (ratio === null && pixels === null) return [];
    return [{ key, ratio: ratio ?? -1, pixels: pixels ?? -1 }];
  });
  entries.sort((left, right) => {
    const ratioDelta = right.ratio - left.ratio;
    if (ratioDelta !== 0) return ratioDelta;
    const pixelDelta = right.pixels - left.pixels;
    return pixelDelta || left.key.localeCompare(right.key);
  });
  const primary = entries[0];
  if (!primary) return "none";
  return primary.ratio >= 0
    ? `${primary.key}:${(primary.ratio * 100).toFixed(1)}%`
    : primary.key;
}

function visualQualityFromEvidence(
  format: ReportExportTarget,
  target: Record<string, unknown>,
  parityProof: Record<string, unknown> | null,
  allPageDifferProof: Record<string, unknown> | null,
): ReportWorkspaceTarget["visualQuality"] {
  const officeQuality = readRecord(
    parityProof?.officeNativeArtifactQualityProof,
  );
  const pdfQuality = readRecord(parityProof?.pdfSemanticArtifactQualityProof);
  const qualityProof = officeQuality ?? pdfQuality;
  const residualProof = readRecord(target.officeVisualResidualAttributionProof);
  const topResidualPage = readRecord(readArray(residualProof?.pages)[0]);
  const parityPass =
    readBoolean(parityProof?.pass) ??
    readBoolean(allPageDifferProof?.pass) ??
    false;
  const qualityPass = readBoolean(qualityProof?.pass) ?? parityPass;
  const rendererFloorPass =
    readBoolean(qualityProof?.officeRendererFloorVisualParityReady) ??
    readBoolean(qualityProof?.officeRendererFloorVisualBandPass) ??
    readBoolean(qualityProof?.pdfRendererFloorVisualBandPass) ??
    readBoolean(qualityProof?.rendererFloorVisualBandPass) ??
    undefined;
  const strictDiffRatio = readNumber(
    qualityProof?.strictExternalRendererDiffRatio,
  );
  const strictMaxPageDiffRatio = readNumber(
    qualityProof?.strictExternalRendererMaxPageDiffRatio,
  );
  const rendererFloorDiffRatio =
    readNumber(qualityProof?.normalizedExternalRendererDiffRatio) ??
    readNumber(qualityProof?.rendererFloorExternalDiffRatio);
  const rendererFloorMaxPageDiffRatio =
    readNumber(qualityProof?.normalizedExternalRendererMaxPageDiffRatio) ??
    readNumber(qualityProof?.rendererFloorExternalMaxPageDiffRatio);
  const rendererFloorTotalLimit =
    readNumber(qualityProof?.normalizedTotalDiffRatioLimit) ??
    readNumber(qualityProof?.rendererFloorTotalDiffRatioLimit);
  const rendererFloorPageLimit =
    readNumber(qualityProof?.normalizedPageDiffRatioLimit) ??
    readNumber(qualityProof?.rendererFloorPageDiffRatioLimit);
  const residualPageIndex = readNumber(topResidualPage?.pageIndex);
  const visualSheetPath = readString(topResidualPage?.visualSheetPath);
  const blockers = uniqueStrings([
    ...readStringList(qualityProof?.blockers),
    ...readStringList(readRecord(target.readiness)?.blockedReasons).filter(
      (blocker) => blocker.toLowerCase().includes("parity"),
    ),
    qualityProof &&
    qualityPass === false &&
    readStringList(qualityProof.blockers).length === 0
      ? `${format.toUpperCase()} visual quality proof did not pass.`
      : "",
    !qualityProof && !parityPass
      ? `${format.toUpperCase()} visual quality proof is missing.`
      : "",
  ]);
  const status: ReportWorkspaceStatus =
    qualityProof || residualProof
      ? qualityPass === false || blockers.length > 0
        ? "blocked"
        : "ready"
      : parityPass
        ? "ready"
        : "missing";

  return {
    status,
    ...(strictDiffRatio !== null ? { strictDiffRatio } : {}),
    ...(strictMaxPageDiffRatio !== null ? { strictMaxPageDiffRatio } : {}),
    ...(rendererFloorDiffRatio !== null ? { rendererFloorDiffRatio } : {}),
    ...(rendererFloorMaxPageDiffRatio !== null
      ? { rendererFloorMaxPageDiffRatio }
      : {}),
    ...(rendererFloorTotalLimit !== null ? { rendererFloorTotalLimit } : {}),
    ...(rendererFloorPageLimit !== null ? { rendererFloorPageLimit } : {}),
    ...(rendererFloorPass !== undefined ? { rendererFloorPass } : {}),
    primaryResidualKind: primaryContributionLabel(
      topResidualPage?.contributionByKind,
    ),
    primaryResidualIssue: primaryContributionLabel(
      topResidualPage?.contributionByIssueClass,
    ),
    ...(residualPageIndex !== null
      ? { topResidualPage: residualPageIndex }
      : {}),
    residualEvidenceRef:
      readString(residualProof?.outputPath) ??
      readString(residualProof?.path) ??
      readString(qualityProof?.schemaVersion) ??
      "not recorded",
    ...(visualSheetPath ? { visualSheetPath } : {}),
    blockers,
  };
}

function nativeDetailFromEvidence(
  format: ReportExportTarget,
  target: Record<string, unknown>,
  writer: Record<string, unknown> | null,
  copy: ReportWorkspaceModelCopy,
): ReportWorkspaceTarget["nativeDetail"] {
  const readback = readRecord(writer?.readback);
  const componentProof = readRecord(target.componentDetailVerificationProof);
  const conversionProof =
    readRecord(writer?.fixedPageLayoutIrTableConversionProof) ??
    readRecord(readback?.fixedPageLayoutIrTableConversionProof);
  const nativeEditabilityProof = readRecord(writer?.nativeEditabilityProof);
  const authorityProof =
    readRecord(nativeEditabilityProof?.tableAuthorityGraphProof) ??
    readRecord(readback?.tableAuthorityGraphProof);
  const componentStatus = readString(componentProof?.status) ?? "missing";
  const componentCoverageReady =
    readBoolean(componentProof?.componentDetailCoverageReady) ?? false;
  const componentSummary = readRecord(componentProof?.componentSummary);
  const topLayerCount = readNumber(componentSummary?.topLayerCount);
  const borderPrimitiveSummary = readRecord(
    conversionProof?.borderPrimitiveSourceSummary,
  );
  const tableAuthorityPass =
    readBoolean(authorityProof?.pass) ??
    readBoolean(readback?.tableAuthorityGraphProofPass);
  const borderPrimitiveAuthorityPass = readBoolean(
    authorityProof?.borderPrimitiveAuthorityPass,
  );
  const blockers = [
    componentStatus === "missing"
      ? `${format.toUpperCase()} component-detail verification is missing.`
      : null,
    componentStatus === "failed" || componentStatus === "skipped"
      ? `${format.toUpperCase()} component-detail verification is ${componentStatus}.`
      : null,
    componentProof && !componentCoverageReady
      ? `${format.toUpperCase()} component-detail coverage is incomplete.`
      : null,
    !conversionProof
      ? `${format.toUpperCase()} table conversion proof is missing.`
      : null,
    tableAuthorityPass === false
      ? `${format.toUpperCase()} table authority graph did not pass.`
      : null,
    borderPrimitiveAuthorityPass === false
      ? `${format.toUpperCase()} border primitive authority did not pass.`
      : null,
  ].filter((value): value is string => Boolean(value));
  const status: ReportWorkspaceStatus =
    blockers.length > 0
      ? componentStatus === "missing" && !conversionProof
        ? "missing"
        : "blocked"
      : componentCoverageReady
        ? "ready"
        : "warn";
  return {
    status,
    componentDetailStatus: componentStatus,
    componentDetailCoverageReady: componentCoverageReady,
    componentSummary:
      topLayerCount === null
        ? componentStatus
        : `${copy.countLayers(topLayerCount)} · ${summarizeCountRecord(componentSummary?.topLayerCountByKind)}`,
    tableConversionRules: summarizeCountRecord(
      conversionProof?.conversionRules,
    ),
    tableConversionProfiles: summarizeCountRecord(
      conversionProof?.conversionProfiles,
    ),
    borderPrimitiveSummary: summarizeCountRecord(
      borderPrimitiveSummary?.byPrimitive,
    ),
    ...(tableAuthorityPass !== null ? { tableAuthorityPass } : {}),
    ...(borderPrimitiveAuthorityPass !== null
      ? { borderPrimitiveAuthorityPass }
      : {}),
    evidenceRef:
      readString(componentProof?.outputPath) ??
      "target.componentDetailVerificationProof",
    blockers,
  };
}

function targetFromEvidence(
  format: ReportExportTarget,
  target: Record<string, unknown>,
  semanticTarget: Record<string, unknown> | null,
  artifact: Record<string, unknown> | null,
  artifactBaseUrl: string | undefined,
  copy: ReportWorkspaceModelCopy,
): ReportWorkspaceTarget {
  const writer = readRecord(target.writer);
  const readback = readRecord(writer?.readback);
  const renderProof = readRecord(target.generatedArtifactRenderProof);
  const parityProof = readRecord(target.productionParityProof);
  const allPageDifferProof = readRecord(target.allPageDifferProof);
  const embeddedRasterParityProof = readRecord(
    target.embeddedRasterParityProof,
  );
  const nativeDetail = nativeDetailFromEvidence(format, target, writer, copy);
  const writerPass = readBoolean(writer?.writerPass) ?? false;
  const readbackPass = readBoolean(readback?.pass) ?? false;
  const renderPass = readBoolean(renderProof?.pass) ?? false;
  const pageCountPass = readBoolean(renderProof?.pageCountPass) ?? false;
  const productionParityPass = readBoolean(parityProof?.pass) ?? false;
  const visualQuality = visualQualityFromEvidence(
    format,
    target,
    parityProof,
    allPageDifferProof,
  );
  const semanticReadbackPass =
    readBoolean(readRecord(semanticTarget?.semanticReadback)?.pass) ??
    readBoolean(semanticTarget?.semanticReadbackPass) ??
    false;
  const productionReady = readBoolean(target.productionReady);
  const artifactUrl =
    readArtifactUrl(target) ?? serviceArtifactUrl(artifact, artifactBaseUrl);
  const artifactDownloadPath =
    readArtifactDownloadPath(target) ?? serviceArtifactDownloadPath(artifact);
  const artifactName =
    readArtifactName(target) ?? serviceArtifactName(artifact);
  const artifactMimeType =
    readArtifactMimeType(target) ??
    serviceArtifactMimeType(artifact) ??
    mimeTypeForTarget(format);
  const artifactId = readString(artifact?.artifactId) ?? undefined;
  const artifactSizeBytes = readNumber(artifact?.sizeBytes) ?? undefined;
  const artifactSha256 = readString(artifact?.sha256) ?? undefined;
  const serviceArtifactPass = artifact
    ? (readBoolean(artifact.pass) ?? false)
    : undefined;
  const artifactReadbackPass = artifact
    ? (readBoolean(readRecord(artifact.readback)?.pass) ?? false)
    : undefined;
  const blockers = [
    !writerPass ? `${format.toUpperCase()} writer did not pass.` : null,
    !readbackPass ? `${format.toUpperCase()} verification did not pass.` : null,
    !renderPass ? `${format.toUpperCase()} render proof did not pass.` : null,
    !pageCountPass
      ? `${format.toUpperCase()} page count proof did not pass.`
      : null,
    !productionParityPass
      ? `${format.toUpperCase()} production parity proof did not pass.`
      : null,
    !semanticReadbackPass
      ? `${format.toUpperCase()} content check did not pass.`
      : null,
    productionReady === false
      ? `${format.toUpperCase()} is not production-ready.`
      : null,
    serviceArtifactPass === false
      ? `${format.toUpperCase()} service artifact did not pass.`
      : null,
    artifactReadbackPass === false
      ? `${format.toUpperCase()} file service check did not pass.`
      : null,
    ...visualQuality.blockers,
  ].filter((value): value is string => Boolean(value));
  const status =
    blockers.length === 0 && productionReady !== false ? "ready" : "blocked";

  return {
    format,
    status,
    ...(artifactId ? { artifactId } : {}),
    ...(artifactUrl ? { artifactUrl } : {}),
    ...(artifactDownloadPath ? { artifactDownloadPath } : {}),
    ...(artifactName ? { artifactName } : {}),
    ...(artifactMimeType ? { artifactMimeType } : {}),
    ...(artifactSizeBytes ? { artifactSizeBytes } : {}),
    ...(artifactSha256 ? { artifactSha256 } : {}),
    ...(serviceArtifactPass !== undefined ? { serviceArtifactPass } : {}),
    ...(artifactReadbackPass !== undefined ? { artifactReadbackPass } : {}),
    writerPass,
    readbackPass,
    renderPass,
    pageCountPass,
    productionParityPass,
    semanticReadbackPass,
    oracle:
      readString(parityProof?.oracle) ??
      readString(allPageDifferProof?.status) ??
      "not recorded",
    writerState:
      readString(writer?.writerState) ??
      (readBoolean(embeddedRasterParityProof?.required)
        ? "visual-preservation-source-raster"
        : "not recorded"),
    visualQuality,
    nativeDetail,
    evidenceItems: [],
    blockers,
  };
}

function missingTarget(
  format: ReportExportTarget,
  copy: ReportWorkspaceModelCopy,
): ReportWorkspaceTarget {
  return {
    format,
    status: "missing",
    writerPass: false,
    readbackPass: false,
    renderPass: false,
    pageCountPass: false,
    productionParityPass: false,
    semanticReadbackPass: false,
    oracle: "missing",
    writerState: "missing",
    visualQuality: {
      status: "missing",
      primaryResidualKind: "missing",
      primaryResidualIssue: "missing",
      residualEvidenceRef: "missing",
      blockers: [copy.blockerTargetVisualMissing(format.toUpperCase())],
    },
    nativeDetail: {
      status: "missing",
      componentDetailStatus: "missing",
      componentDetailCoverageReady: false,
      componentSummary: "missing",
      tableConversionRules: "missing",
      tableConversionProfiles: "missing",
      borderPrimitiveSummary: "missing",
      evidenceRef: "missing",
      blockers: [copy.blockerTargetNativeDetailMissing(format.toUpperCase())],
    },
    evidenceItems: [],
    blockers: [copy.blockerTargetExportMissing(format.toUpperCase())],
  };
}

function statusFromOptionalPass(
  value: boolean | undefined,
): ReportWorkspaceStatus {
  if (value === undefined) return "missing";
  return value ? "ready" : "blocked";
}

function stableEvidenceRef(value: string | undefined): string | null {
  if (!value) return null;
  const normalized = value.trim();
  if (
    !normalized ||
    normalized === "missing" ||
    normalized === "not recorded"
  ) {
    return null;
  }
  return normalized;
}

function visualEvidenceUrl(target: ReportWorkspaceTarget): string | undefined {
  const ref = stableEvidenceRef(
    target.visualQuality.visualSheetPath ??
      target.visualQuality.residualEvidenceRef,
  );
  return readUrl(ref) ?? undefined;
}

function buildTargetEvidenceItems(
  target: ReportWorkspaceTarget,
  chartInspector: ReportChartSemanticInspectorModel,
  copy: ReportWorkspaceModelCopy,
): ReportWorkspaceEvidenceItem[] {
  const artifactRef =
    stableEvidenceRef(target.artifactUrl) ??
    stableEvidenceRef(target.artifactDownloadPath) ??
    stableEvidenceRef(target.artifactId) ??
    stableEvidenceRef(target.artifactName);
  const artifactStatus =
    target.serviceArtifactPass === false ||
    target.artifactReadbackPass === false
      ? "blocked"
      : artifactRef
        ? "ready"
        : "missing";
  const artifactItems: ReportWorkspaceEvidenceItem[] = artifactRef
    ? [
        {
          id: `${target.format}-native-artifact`,
          kind: "native-artifact",
          label: copy.evidenceNativeArtifact(target.format.toUpperCase()),
          status: artifactStatus,
          ref: artifactRef,
          ...(target.artifactUrl ? { url: target.artifactUrl } : {}),
          ...(target.artifactSha256 ? { sha256: target.artifactSha256 } : {}),
          ...(target.artifactSizeBytes
            ? { sizeBytes: target.artifactSizeBytes }
            : {}),
          ...(target.artifactName ? { detail: target.artifactName } : {}),
        },
      ]
    : [];
  const readbackRef =
    stableEvidenceRef(target.artifactId) ??
    stableEvidenceRef(target.artifactDownloadPath) ??
    stableEvidenceRef(target.artifactUrl);
  const readbackPass =
    target.serviceArtifactPass === undefined &&
    target.artifactReadbackPass === undefined
      ? undefined
      : target.serviceArtifactPass !== false &&
        target.artifactReadbackPass !== false;
  const artifactReadbackItems: ReportWorkspaceEvidenceItem[] = readbackRef
    ? [
        {
          id: `${target.format}-artifact-readback`,
          kind: "artifact-readback",
          label: copy.evidenceFileCheck,
          status: statusFromOptionalPass(readbackPass),
          ref: readbackRef,
          detail:
            target.artifactReadbackPass === undefined
              ? copy.detailServiceArtifact
              : target.artifactReadbackPass
                ? copy.detailCheckPassed
                : copy.detailCheckBlocked,
        },
      ]
    : [];
  const nativeDetailRef = stableEvidenceRef(target.nativeDetail.evidenceRef);
  const visualRef = stableEvidenceRef(
    target.visualQuality.visualSheetPath ??
      target.visualQuality.residualEvidenceRef,
  );
  const chartReadbackItems = chartInspector.nativeReadbackProofs
    .filter((proof) => proof.target === target.format)
    .map(
      (proof, index): ReportWorkspaceEvidenceItem => ({
        id: `${target.format}-chart-readback-${index + 1}`,
        kind: "chart-readback",
        label: copy.evidenceChartCheck,
        status: proof.status,
        ref: proof.evidenceRef,
        detail: proof.chartRefs.join(", ") || copy.detailChartProof,
      }),
    );

  return [
    ...artifactItems,
    ...artifactReadbackItems,
    ...(nativeDetailRef
      ? [
          {
            id: `${target.format}-native-detail`,
            kind: "native-detail",
            label: copy.evidenceComponentDetail,
            status: target.nativeDetail.status,
            ref: nativeDetailRef,
            detail: target.nativeDetail.componentSummary,
          } satisfies ReportWorkspaceEvidenceItem,
        ]
      : []),
    ...(visualRef
      ? [
          {
            id: `${target.format}-visual-residual`,
            kind: "visual-residual",
            label: copy.evidenceVisualResidual,
            status: target.visualQuality.status,
            ref: visualRef,
            ...(visualEvidenceUrl(target)
              ? { url: visualEvidenceUrl(target) }
              : {}),
            detail: target.visualQuality.primaryResidualIssue,
          } satisfies ReportWorkspaceEvidenceItem,
        ]
      : []),
    ...chartReadbackItems,
    {
      id: `${target.format}-semantic-readback`,
      kind: "semantic-readback",
      label: copy.evidenceContentCheck,
      status: target.semanticReadbackPass ? "ready" : "blocked",
      ref: `${target.format}.semanticReadback`,
      detail: target.semanticReadbackPass
        ? copy.detailNativeSemanticPass
        : copy.detailMissing,
    },
  ];
}

export function readReportWorkspaceEvidence(
  project: unknown,
): Record<string, unknown> | null {
  const candidate = readFirstDeep(project, [
    ["reportWorkspace"],
    ["reportWorkspaceEvidence"],
    ["documentExportProductionGate"],
    ["documentExportEvidence"],
    ["reportExportProductionGate"],
    ["generationEvidence", "reportWorkspace"],
    ["generationEvidence", "reportWorkspaceEvidence"],
    ["generationEvidence", "documentExportProductionGate"],
    ["generationEvidence", "documentExportEvidence"],
    ["generationEvidence", "reportExportProductionGate"],
    ["reportExportReadiness", "productionGate"],
    ["documentExportReadiness", "productionGate"],
    ["metadata", "reportWorkspace"],
    ["metadata", "reportWorkspaceEvidence"],
    ["metadata", "documentExportProductionGate"],
    ["metadata", "documentExportEvidence"],
    ["metadata", "reportExportProductionGate"],
    ["manifest", "reportWorkspace"],
    ["manifest", "reportWorkspaceEvidence"],
    ["manifest", "documentExportProductionGate"],
    ["manifest", "documentExportEvidence"],
    ["manifest", "reportExportProductionGate"],
  ]);
  return readRecord(candidate);
}

export function isReportWorkspaceProject(project: unknown): boolean {
  if (readReportWorkspaceEvidence(project)) return true;
  const applicationKind = readString(
    readFirstDeep(project, [
      ["applicationKind"],
      ["manifest", "applicationKind"],
      ["metadata", "applicationKind"],
    ]),
  );
  if (applicationKind?.includes("report")) return true;
  return Boolean(
    readFirstDeep(project, [
      ["reportExportReadiness"],
      ["reportReadiness"],
      ["documentExportReadiness"],
      ["metadata", "reportExportReadiness"],
      ["manifest", "reportExportReadiness"],
    ]),
  );
}

export function buildReportWorkspaceModel(
  project: unknown,
  options: { selectedTarget?: ReportExportTarget; locale?: LocaleCode } = {},
): ReportWorkspaceModel {
  // Locale defaults to `en` so every existing caller (and the model's own
  // tests) keeps the English output it asserts today; the report surface
  // passes the operator's locale so nothing this builder composes reaches a
  // screen in a language the operator did not choose.
  const copy = getReportWorkspaceModelCopy(options.locale);
  const evidence = readReportWorkspaceEvidence(project);
  const serviceManifest = readReportWorkspaceServiceManifest(project);
  const productionInput = buildReportProductionInput(
    project,
    evidence,
    serviceManifest,
    copy,
  );
  const formulaOracle = buildUniverFormulaOracleModel(project);
  const chartInspector = buildReportChartSemanticInspector(
    project,
    formulaOracle,
    copy,
  );
  const artifactsByFormat = serviceArtifactMap(serviceManifest);
  const artifactBaseUrl = readDocumentExportServiceBaseUrl(
    project,
    serviceManifest,
  );
  const artifactService = readServiceArtifactSummary(serviceManifest);
  const exportHistory = serviceExportHistory(
    serviceManifest,
    artifactBaseUrl,
    copy,
  );
  const summary = readRecord(evidence?.documentModelSummary);
  const source = readRecord(summary?.source);
  const summaryPageCount = Math.max(
    0,
    Math.floor(readNumber(summary?.pageCount) ?? 0),
  );
  const targetRecords = readArray(evidence?.targets).flatMap((entry) => {
    const record = readRecord(entry);
    const format = normalizeTarget(record?.format);
    return record && format ? [{ format, record }] : [];
  });
  const semantic = readRecord(evidence?.semanticEditRoundtripProof);
  const semanticTargets = new Map<ReportExportTarget, Record<string, unknown>>(
    readArray(semantic?.targets).flatMap((entry) => {
      const record = readRecord(entry);
      const format = normalizeTarget(record?.format);
      return record && format ? [[format, record] as const] : [];
    }),
  );
  const byFormat = new Map(
    targetRecords.map((entry) => [entry.format, entry.record]),
  );
  const baseTargets = REPORT_EXPORT_TARGETS.map((format) => {
    const target = byFormat.get(format);
    return target
      ? targetFromEvidence(
          format,
          target,
          semanticTargets.get(format) ?? null,
          artifactsByFormat.get(format) ?? null,
          artifactBaseUrl,
          copy,
        )
      : missingTarget(format, copy);
  });
  const targets = baseTargets.map((target) => ({
    ...target,
    evidenceItems: buildTargetEvidenceItems(target, chartInspector, copy),
  }));
  const selectedTarget =
    options.selectedTarget ??
    targets.find((target) => target.status !== "missing")?.format ??
    "pdf";
  const readyTargetCount = targets.filter(
    (target) => target.status === "ready",
  ).length;
  const targetCount = targets.length;
  const productionReady = readBoolean(evidence?.productionReady) ?? false;
  const semanticPass =
    readBoolean(semantic?.pass) ??
    targets.every((target) => target.semanticReadbackPass);
  const status = evidence
    ? mergeStatus([
        productionReady ? "ready" : "blocked",
        semanticPass ? "ready" : "blocked",
        productionInput.status,
        formulaOracle.status === "blocked" ? "blocked" : "ready",
        ...targets.map((target) => target.status),
      ])
    : "missing";
  const targetBlockers = targets.flatMap((target) => target.blockers);
  const blockers = [
    !evidence ? copy.blockerExportEvidenceMissing : null,
    evidence && !productionReady ? copy.blockerProductionGateNotReady : null,
    evidence && !semanticPass ? copy.blockerSemanticProofNotReady : null,
    serviceManifest && artifactService.status !== "ready"
      ? copy.blockerArtifactServiceNotReady
      : null,
    formulaOracle.status === "blocked"
      ? copy.blockerFormulaOracleNotReady
      : null,
    ...formulaOracle.blockers,
    ...targetBlockers,
  ].filter((value): value is string => Boolean(value));
  const publishBlockers = [...blockers, ...productionInput.blockers];
  const pages = buildReportWorkspacePages(
    project,
    evidence,
    summary,
    summaryPageCount,
    copy,
  );
  const pageCount = summaryPageCount || pages.length;

  return {
    schemaVersion: "studio-report-workspace-model.v1",
    status,
    title:
      readString(
        readFirstDeep(project, [
          ["manifest", "name"],
          ["metadata", "name"],
          ["name"],
        ]),
      ) ?? copy.fallbackTitle,
    sourceKind:
      readString(source?.canonicalInput) ??
      readString(source?.standardBasis) ??
      "cogo-document-model",
    pageCount,
    pages,
    nodeCount: readNumber(summary?.nodeCount) ?? 0,
    targetCount,
    readyTargetCount,
    selectedTarget,
    targets,
    semanticEdit: {
      pass: semanticPass,
      expectedText: readString(semantic?.expectedText) ?? "not recorded",
      operation:
        readString(readRecord(semantic?.editPlan)?.operation) ?? "replace-text",
      replacementCount:
        readNumber(readRecord(semantic?.editPlan)?.replacementCount) ?? 0,
    },
    artifactService,
    formulaOracle,
    chartInspector,
    productionInput,
    exportHistory,
    lanes: [
      {
        id: "production-input",
        label: copy.laneProductionInput,
        status: productionInput.status,
        detail:
          productionInput.status === "ready"
            ? copy.laneDetailAnalyzedBy(
                productionInput.uploadFileName,
                productionInput.analysisJobId,
              )
            : (productionInput.blockers[0] ?? productionInput.mode),
      },
      {
        id: "source-corpus",
        label: copy.laneSourceCorpus,
        status: evidence ? "ready" : "missing",
        detail: evidence
          ? copy.laneDetailSourceLoaded(
              readString(source?.canonicalInput) ?? "cogo-document-model",
            )
          : copy.laneDetailSourceMissing,
      },
      {
        id: "production-export",
        label: copy.laneProductionExport,
        status: statusFromBoolean(productionReady),
        detail: copy.laneDetailTargetsReady(readyTargetCount, targetCount),
      },
      {
        id: "semantic-roundtrip",
        label: copy.laneSemanticRoundtrip,
        status: statusFromBoolean(semanticPass),
        detail: semanticPass
          ? copy.laneDetailSemanticPass
          : copy.laneDetailSemanticFail,
      },
      {
        id: "artifact-service",
        label: copy.laneArtifactService,
        status: artifactService.status,
        detail:
          artifactService.status === "missing"
            ? copy.laneDetailServiceMissing
            : copy.laneDetailArtifactsPass(
                artifactService.passArtifactCount,
                artifactService.artifactCount,
              ),
      },
      {
        id: "formula-oracle",
        label: copy.laneFormulaOracle,
        status: formulaOracle.status,
        detail: copy.laneDetailCases(
          formulaOracle.passCount,
          formulaOracle.caseCount,
          formulaOracle.enginePackage,
        ),
      },
      {
        id: "chart-semantics",
        label: copy.laneChartSemantics,
        status: chartInspector.status,
        detail: copy.laneDetailCharts(
          chartInspector.chartCount,
          chartInspector.seriesCount,
          chartInspector.univerPreview.mode,
        ),
      },
      {
        id: "publish-readiness",
        label: copy.lanePublishReadiness,
        status: publishBlockers.length === 0 ? "ready" : "blocked",
        detail:
          publishBlockers.length === 0
            ? copy.laneDetailNoBlockers
            : (publishBlockers[0] ?? copy.laneDetailBlocked),
      },
    ],
    exportBlocked: blockers.length > 0,
    publishBlocked: publishBlockers.length > 0,
    blockers,
    evidenceRefs: [
      "docs/agentic/evidence/cogo-document-export-production/cogo-document-export-production-gate.v1.json",
      "docs/agentic/evidence/cogo-document-export-production/cogo-document-export-production-check.v1.json",
      "docs/agentic/evidence/cogo-document-source-corpus-production/cogo-document-source-corpus-production-summary.v4.json",
      productionInput.evidenceRef,
      ...(serviceManifest
        ? [
            readString(serviceManifest.evidenceRef) ??
              readString(serviceManifest.outputPath) ??
              readString(serviceManifest.path) ??
              "documentExportServiceManifest",
          ]
        : []),
      ...(chartInspector.evidenceRef !== "missing"
        ? [chartInspector.evidenceRef]
        : []),
    ],
  };
}

export function applyReportWorkspaceSemanticEdit(
  project: Record<string, unknown>,
  replacementText: string,
): Record<string, unknown> {
  const metadata = readRecord(project.metadata) ?? {};
  const manifest = readRecord(project.manifest) ?? {};
  const existing = readRecord(metadata.reportWorkspaceEdits) ?? {};
  const edits = readArray(existing.edits);
  const editPacket = {
    schemaVersion: "studio-report-workspace-edits.v1",
    updatedAt: new Date().toISOString(),
    edits: [
      ...edits,
      {
        id: `semantic-edit-${edits.length + 1}`,
        operation: "replace-text",
        target: "documentModel.semanticLayer",
        replacementText,
      },
    ],
  };
  return {
    ...project,
    manifest: {
      ...manifest,
      reportWorkspaceEdits: editPacket,
    },
    metadata: {
      ...metadata,
      reportWorkspaceEdits: editPacket,
    },
  };
}

function safeDownloadName(value: string): string {
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9가-힣]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "cogo-report";
}

export function buildReportWorkspaceExportArtifact(
  project: unknown,
  target: ReportExportTarget,
): ReportWorkspaceExportArtifact {
  const model = buildReportWorkspaceModel(project, { selectedTarget: target });
  const targetReadiness =
    model.targets.find((entry) => entry.format === target) ?? model.targets[0]!;
  return {
    schemaVersion: "studio-report-workspace-export-artifact.v1",
    generatedAt: new Date().toISOString(),
    target,
    title: model.title,
    sourceKind: model.sourceKind,
    status: targetReadiness.status,
    pageCount: model.pageCount,
    pages: model.pages,
    nodeCount: model.nodeCount,
    semanticEdit: model.semanticEdit,
    formulaOracle: model.formulaOracle,
    chartInspector: model.chartInspector,
    productionInput: model.productionInput,
    targetReadiness,
    blockers: targetReadiness.blockers,
    evidenceRefs: model.evidenceRefs,
    ...(targetReadiness.artifactUrl ||
    targetReadiness.artifactName ||
    targetReadiness.artifactMimeType
      ? {
          nativeArtifact: {
            ...(targetReadiness.artifactUrl
              ? { url: targetReadiness.artifactUrl }
              : {}),
            ...(targetReadiness.artifactName
              ? { fileName: targetReadiness.artifactName }
              : {}),
            ...(targetReadiness.artifactMimeType
              ? { mimeType: targetReadiness.artifactMimeType }
              : {}),
          },
        }
      : {}),
    note: "This artifact is the Studio export control packet. Native bytes are served by the linked document/export writer when nativeArtifact.url is present.",
  };
}

export function downloadReportWorkspaceExportArtifact(
  project: unknown,
  target: ReportExportTarget,
): { mode: "native-url" | "evidence-packet"; fileName: string } {
  const artifact = buildReportWorkspaceExportArtifact(project, target);
  const fileName =
    artifact.nativeArtifact?.fileName ??
    `${safeDownloadName(artifact.title)}-${target}-export-evidence.json`;
  const documentRef = typeof document === "undefined" ? null : document;

  if (documentRef && artifact.nativeArtifact?.url) {
    const anchor = documentRef.createElement("a");
    anchor.href = artifact.nativeArtifact.url;
    anchor.download = fileName;
    anchor.rel = "noopener";
    documentRef.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return { mode: "native-url", fileName };
  }

  if (!documentRef || typeof URL === "undefined") {
    return { mode: "evidence-packet", fileName };
  }

  const blob = new Blob([JSON.stringify(artifact, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = documentRef.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  documentRef.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return { mode: "evidence-packet", fileName };
}
